import { useEffect, useMemo, useState } from "react";
import TimelineBlock from "./TimelineBlock";
import { clamp, snap } from "./TimelineUtils";
import { uid } from "./uid";
import { Clock, Timer } from "lucide-react";

const PX_PER_MIN = 8;
const ROW_HEIGHT = 100;
const MIN_DURATION = 1;
const VIEWPORT_MINUTES = 180;

function normalizeSequentialBlocks(blocks) {
  const sorted = [...blocks].sort((a, b) => (a.startMin || 0) - (b.startMin || 0));
  let cursor = 0;
  return sorted.map((b, i) => {
    const isLast = i === sorted.length - 1;
    const rest = isLast ? 0 : (b.restDurationMin || 0);
    const next = { ...b, startMin: cursor, restDurationMin: rest };
    cursor += Math.max(MIN_DURATION, b.durationMin || 0) + rest;
    return next;
  });
}

export default function SessionTimeline({ 
  blocks, 
  onChange, 
  isDarkMode,
  selectedBlockUids = new Set(),
  onSelectBlock,
}) {
  const [dragState, setDragState] = useState(null);

  const card = isDarkMode ? "bg-gray-900 border-gray-700" : "bg-white border-gray-200";
  const gridLine = isDarkMode ? "rgba(255,255,255,0.08)" : "rgba(17,24,39,0.1)";
  const muted = isDarkMode ? "text-gray-400" : "text-gray-600";

  const maxEnd = useMemo(
    () =>
      Math.max(
        60,
        ...(blocks || []).map(
          (b) => (b.startMin || 0) + (b.durationMin || 0)
        )
      ),
    [blocks]
  );

  const totalMinutes = Math.max(
    VIEWPORT_MINUTES,
    Math.ceil(maxEnd / 15) * 15 + 30
  );

  const totalWidthPx = totalMinutes * PX_PER_MIN;
  
  const totalDuration = useMemo(
    () => (blocks || []).reduce((s, b) => s + (b.durationMin || 0) + (b.restDurationMin || 0), 0),
    [blocks]
  );

  const ticks = useMemo(() => {
    const arr = [];
    for (let m = 0; m <= totalMinutes; m += 5) arr.push(m);
    return arr;
  }, [totalMinutes]);

  useEffect(() => {
    const onMove = (e) => {
      if (!dragState) return;

      const dx = e.clientX - dragState.startX;
      const deltaMin = dx / PX_PER_MIN;

      const next = (blocks || []).map((b) => {
        if (b.uid !== dragState.uid) return b;

        if (dragState.mode === "resize") {
          const nextDuration = snap(dragState.startDurationMin + deltaMin, 1);
          return { ...b, durationMin: Math.max(MIN_DURATION, nextDuration) };
        } else {
          const nextStart = snap(dragState.startStartMin + deltaMin, 1);
          return { ...b, startMin: clamp(nextStart, 0, totalMinutes) };
        }
      });

      onChange(normalizeSequentialBlocks(next));
    };

    const onUp = (e) => {
      try {
        e?.target?.releasePointerCapture?.(e.pointerId);
      } catch {}
      setDragState(null);
    };

    window.addEventListener("pointermove", onMove, { passive: false });
    window.addEventListener("pointerup", onUp, { passive: false });
    return () => {
      window.removeEventListener("pointermove", onMove);
      window.removeEventListener("pointerup", onUp);
    };
  }, [dragState, blocks, onChange, totalMinutes]);

  const onDrop = (e) => {
    e.preventDefault();
    const raw = e.dataTransfer.getData("application/x-protocol-block");
    if (!raw) return;

    const p = JSON.parse(raw);

    let startMin = 0;
    if (blocks && blocks.length > 0) {
      const lastBlock = blocks[blocks.length - 1];
      startMin = (lastBlock.startMin || 0) + (lastBlock.durationMin || 0) + (lastBlock.restDurationMin || 0);
    }

    const next = normalizeSequentialBlocks([
      ...(blocks || []).map((b, i, arr) => 
        i === arr.length - 1 && (b.restDurationMin || 0) === 0
          ? { ...b, restDurationMin: 5 }
          : b
      ),
      {
        uid: uid("b_"),
        protocol_template_id: p.id,
        label: p.name,
        startMin: startMin,
        durationMin: p.default_duration_min || 15,
        restDurationMin: 0,
        intensity: p.default_intensity || null,
      },
    ]);

    onChange(next);
  };

  return (
    <div className={`rounded-2xl border ${card} overflow-hidden`}>
      {/* Header */}
      <div className="px-4 py-3 border-b border-gray-700/30">
        <div className="flex items-center justify-between">
          <div>
            <p className="text-sm font-semibold">Session Timeline</p>
            <p className={`text-xs ${muted}`}>
              Drag blocks from the library. Click to select and edit below.
            </p>
          </div>
          <div className="flex items-center gap-4">
            <div className={`flex items-center gap-1.5 text-xs ${muted}`}>
              <Clock size={14} />
              <span>{(blocks || []).length} blocks</span>
            </div>
            <div className={`flex items-center gap-1.5 px-2 py-1 rounded-lg ${
              isDarkMode ? "bg-emerald-500/20 text-emerald-400" : "bg-emerald-500/10 text-emerald-600"
            }`}>
              <Timer size={14} />
              <span className="text-xs font-semibold">{totalDuration} min</span>
            </div>
          </div>
        </div>
      </div>

      {/* Timeline area */}
      <div
        className="relative overflow-x-auto"
        style={{ height: ROW_HEIGHT + 24 }}
        onDragOver={(e) => e.preventDefault()}
        onDrop={onDrop}
        onClick={() => onSelectBlock?.(null)} // Deselect when clicking empty area
      >
        <div
          className="relative"
          style={{
            height: ROW_HEIGHT + 24,
            width: totalWidthPx,
            minWidth: "100%",
          }}
        >
          {/* Grid lines */}
          {ticks.map((m) => {
            const x = m * PX_PER_MIN;
            const isMajor = m % 30 === 0;
            const isHour = m % 60 === 0;
            return (
              <div
                key={m}
                className="absolute top-0 bottom-0"
                style={{
                  left: x,
                  width: 1,
                  background: gridLine,
                  opacity: isHour ? 1 : isMajor ? 0.7 : 0.4,
                }}
              />
            );
          })}

          {/* Time labels */}
          {ticks
            .filter((m) => m % 15 === 0)
            .map((m) => {
              const isHour = m % 60 === 0;
              return (
                <div
                  key={`lbl-${m}`}
                  className={`absolute top-1 text-[10px] ${muted} ${isHour ? "font-semibold" : ""}`}
                  style={{ left: m * PX_PER_MIN + 4 }}
                >
                  {m}′
                </div>
              );
            })}

          {/* Blocks */}
          {(blocks || []).map((b) => (
            <TimelineBlock
              key={b.uid}
              block={b}
              pxPerMin={PX_PER_MIN}
              rowHeight={ROW_HEIGHT}
              isDarkMode={isDarkMode}
              isActive={dragState?.uid === b.uid}
              isSelected={selectedBlockUids.has(b.uid)}
              onPointerDownMove={(e) =>
                setDragState({
                  uid: b.uid,
                  mode: "move",
                  startX: e.clientX,
                  startStartMin: b.startMin || 0,
                  startDurationMin: b.durationMin || 0,
                })
              }
              onPointerDownResize={(e) =>
                setDragState({
                  uid: b.uid,
                  mode: "resize",
                  startX: e.clientX,
                  startStartMin: b.startMin || 0,
                  startDurationMin: b.durationMin || 0,
                })
              }
              onRemove={() =>
                onChange(
                  normalizeSequentialBlocks(
                    (blocks || []).filter((x) => x.uid !== b.uid)
                  )
                )
              }
              onClick={(e) => onSelectBlock?.(b.uid, e?.shiftKey)}
            />
          ))}

          {/* Empty state */}
          {(!blocks || blocks.length === 0) && (
            <div className="absolute inset-0 flex items-center justify-center">
              <div className={`text-center ${muted}`}>
                <p className="text-sm">Drop training blocks here</p>
                <p className="text-xs mt-1">Drag from the library on the left</p>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
